// PostHog usage events from the 2D demo viewer.
// Sends only through trackEvent, and never throws into the page.
import { trackEvent } from '../analytics';

export const VIEWER_EVENTS = {
  opened: 'viewer_opened',
  roundPlayed: 'viewer_round_played',
  seeked: 'viewer_seeked',
  sessionEnded: 'viewer_session_ended',
  modeSwitched: 'viewer_mode_switched',
};

const ROUND_PLAYED_MS = 3000;
const SEEK_BURST_MS = 1500;

// Where the viewer was opened from. `historyBack` is vue-router's history.state.back; a
// same-origin referrer covers a new tab (MatchRestrat.vue). Never returns a raw path.
export function entryFrom(historyBack, referrer, origin) {
  let path = historyBack;
  if (!path && referrer) {
    try {
      const url = new URL(referrer);
      if (url.origin === origin) path = url.pathname;
    } catch (e) {
      // Not a URL: treated as no referrer.
    }
  }
  if (!path) return 'direct';
  if (/^\/demos\/[^/?#]+\/?(?:[?#]|$)/.test(path)) return 'demo_page';
  if (/^\/matches\/pro_series\/[^/?#]+/.test(path)) return 'pro_series';
  if (/^\/matches\/[^/?#]+/.test(path)) return 'match_page';
  return 'other';
}

function directionOf(net) {
  if (net > 0) return 'forward';
  if (net < 0) return 'backward';
  return 'none';
}

export function createViewerSession({ demoId, entry, now = () => window.performance.now() }) {
  const common = {
    viewer_session_id: window.crypto.randomUUID(),
    demo_id: String(demoId),
    map: null,
    viewer_mode: '2d',
    entry,
  };
  const startedAt = now();

  let isOpened = false;
  let isEnded = false;
  let isPlaying = false;
  let isVisible = true;
  let round = null;
  let frameRate = 64;

  const playedMs = new Map();
  const roundsPlayed = new Set();
  const roundsViewed = new Set();
  let watchMs = 0;
  let segmentStart = null;
  let playTimer = null;

  let burst = null;
  let burstTimer = null;
  let seekTotal = 0;

  function send(name, properties) {
    try {
      trackEvent(name, { ...common, ...properties });
    } catch (e) {
      // Tracking must never break the viewer.
    }
  }

  function closeSegment() {
    clearTimeout(playTimer);
    playTimer = null;
    if (segmentStart === null) return;
    const ms = now() - segmentStart;
    segmentStart = null;
    watchMs += ms;
    playedMs.set(round, (playedMs.get(round) || 0) + ms);
  }

  // Play time counts only while playing and visible. The 3 s check is a timer, not a frame hook.
  function openSegment() {
    if (!isOpened || isEnded || !isPlaying || !isVisible || segmentStart !== null) return;
    segmentStart = now();
    if (roundsPlayed.has(round)) return;
    const left = Math.max(0, ROUND_PLAYED_MS - (playedMs.get(round) || 0));
    // eslint-disable-next-line no-use-before-define
    playTimer = setTimeout(roundPlayed, left);
  }

  function roundPlayed() {
    closeSegment();
    roundsPlayed.add(round);
    send(VIEWER_EVENTS.roundPlayed, { round_number: round, frame_rate: frameRate });
    openSegment();
  }

  function update(change) {
    closeSegment();
    change();
    openSegment();
  }

  function flushSeeks() {
    clearTimeout(burstTimer);
    burstTimer = null;
    if (!burst) return;
    const done = burst;
    burst = null;
    send(VIEWER_EVENTS.seeked, {
      seek_count: done.count,
      method: done.method,
      direction: directionOf(done.net),
      round_number: done.round,
    });
  }

  return {
    opened({ map, roundNumber, totalRounds }) {
      if (isOpened || isEnded) return;
      isOpened = true;
      common.map = map ?? null;
      round = roundNumber;
      roundsViewed.add(round);
      send(VIEWER_EVENTS.opened, {
        load_ms: Math.max(0, Math.round(now() - startedAt)),
        round_number: roundNumber,
        total_rounds: totalRounds,
      });
      openSegment();
    },

    playing(value) {
      update(() => { isPlaying = value; });
    },

    visible(value) {
      update(() => { isVisible = value; });
    },

    roundShown(roundNumber) {
      update(() => {
        round = roundNumber;
        if (isOpened) roundsViewed.add(round);
      });
    },

    frameRateChanged(value) {
      frameRate = value;
    },

    // A keyboard jump or a slider drag, in frames. A drag back to its start is not a seek.
    seeked({ method, from, to }) {
      if (!isOpened || isEnded || from === to) return;
      if (!burst) burst = { count: 0, method, net: 0 };
      burst.count += 1;
      if (burst.method !== method) burst.method = 'mixed';
      burst.net += to - from;
      burst.round = round;
      seekTotal += 1;
      clearTimeout(burstTimer);
      burstTimer = setTimeout(flushSeeks, SEEK_BURST_MS);
    },

    // onBeforeUnmount or pagehide, whichever comes first.
    end() {
      if (isEnded) return;
      isEnded = true;
      flushSeeks();
      closeSegment();
      if (!isOpened) return;
      send(VIEWER_EVENTS.sessionEnded, {
        watch_seconds: Math.round(watchMs / 100) / 10,
        rounds_played: roundsPlayed.size,
        rounds_viewed: roundsViewed.size,
        seek_count: seekTotal,
      });
    },

    // Declared for the 3D toggle; nothing calls it yet.
    modeSwitched(from, to) {
      send(VIEWER_EVENTS.modeSwitched, { from_mode: from, to_mode: to });
      common.viewer_mode = to;
    },
  };
}
